/**
 * ITEM CONFIRM SERVICE (web)
 *
 * Community confidence on a catalog entry: a signed-in user can "Confirm"
 * that an item is real and correctly listed. Two storage collections,
 * routed by itemType inside every function so callers don't need to know:
 *
 *   - Polish confirms live in `polishConfirms` (id = `{slug}__{uid}`).
 *     This collection predates the multi-itemType feature; keeping it in
 *     place means the trigger-maintained `polishCatalog.confirmCount`
 *     stays accurate and the brand-page confirmed-badge lookup keeps
 *     working. Migrating polish confirmations into `itemConfirms` would
 *     have required moving both the data and the trigger and would have
 *     added launch risk for zero user-visible benefit.
 *   - Non-polish confirms live in `itemConfirms`
 *     (id = `{itemType}__{slug}__{uid}`, plus itemType/slug fields for
 *     querying). New in Phase 6.
 *
 * Every function takes an optional `itemType` parameter that defaults to
 * `'polish'`, so existing call sites keep working without changes.
 * `confirmPolish`/`unconfirmPolish` names are kept as-is for the same
 * backwards-compat reason — under the hood they now route by itemType.
 */

import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { slugify } from '../utils/polishSlug';
import type { ItemType } from '../types';

/** Compute doc id per collection. Polish uses `{slug}__{uid}`; non-polish
 *  includes the itemType up front so a single flat collection can host
 *  every non-polish type without id collisions between, say, a plate and
 *  a powder that happen to share the same name+brand. */
const polishConfirmId = (slug: string, uid: string) => `${slug}__${uid}`;
const itemConfirmId = (itemType: ItemType, slug: string, uid: string) =>
  `${itemType}__${slug}__${uid}`;

/** Minimum confirms before an item shows the community-confirmed badge in grids. */
export const COMMUNITY_VERIFIED_MIN = 1;

/** slug → community confirm count for a brand's catalog polishes (one query).
 *  Polish-only intentionally — this powers the confirm badge in polish
 *  timeline grids, which non-polish types don't have a UI hook for yet. */
export async function getBrandConfirmCounts(brandName: string): Promise<Map<string, number>> {
  const brandSlug = slugify(brandName);
  const out = new Map<string, number>();
  if (!brandSlug) return out;
  const snap = await getDocs(query(collection(db, 'polishCatalog'), where('brandSlug', '==', brandSlug)));
  snap.docs.forEach((d) => {
    const c = (d.data() as { confirmCount?: number }).confirmCount;
    if (typeof c === 'number' && c > 0) out.set(d.id, c);
  });
  return out;
}

export interface ConfirmState {
  count: number;
  confirmed: boolean;
}

/** Confirm count for an item + whether the current user has confirmed. */
export async function getConfirmState(
  slug: string,
  uid?: string | null,
  itemType: ItemType = 'polish'
): Promise<ConfirmState> {
  if (!slug) return { count: 0, confirmed: false };
  if (itemType === 'polish') {
    const snap = await getDocs(query(collection(db, 'polishConfirms'), where('slug', '==', slug)));
    const confirmed = uid ? snap.docs.some((d) => d.id === polishConfirmId(slug, uid)) : false;
    return { count: snap.size, confirmed };
  }
  const snap = await getDocs(
    query(
      collection(db, 'itemConfirms'),
      where('itemType', '==', itemType),
      where('slug', '==', slug)
    )
  );
  const confirmed = uid ? snap.docs.some((d) => d.id === itemConfirmId(itemType, slug, uid)) : false;
  return { count: snap.size, confirmed };
}

export async function confirmPolish(
  slug: string,
  uid: string,
  itemType: ItemType = 'polish'
): Promise<void> {
  if (itemType === 'polish') {
    await setDoc(doc(db, 'polishConfirms', polishConfirmId(slug, uid)), {
      slug,
      userId: uid,
      createdAt: serverTimestamp(),
    });
    return;
  }
  await setDoc(doc(db, 'itemConfirms', itemConfirmId(itemType, slug, uid)), {
    itemType,
    slug,
    userId: uid,
    createdAt: serverTimestamp(),
  });
}

export async function unconfirmPolish(
  slug: string,
  uid: string,
  itemType: ItemType = 'polish'
): Promise<void> {
  if (itemType === 'polish') {
    await deleteDoc(doc(db, 'polishConfirms', polishConfirmId(slug, uid)));
    return;
  }
  await deleteDoc(doc(db, 'itemConfirms', itemConfirmId(itemType, slug, uid)));
}
